import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
} from 'core/userContext';
import { AlertStatus } from 'modules/campaigns/alerts/constants';
import { useProjectCampaignAlertsQuery } from 'modules/campaigns/alerts/hooks';
import { useCampaignContext } from 'modules/campaigns/editor/context/CampaignContext';
import { useCampaignsHasRecentTriggeredAlerts } from 'modules/campaigns/lib';
import { RECENT_ALERTS_DURATION_WINDOW } from 'modules/campaigns/lib/constants';
import { getAlertDetails, getAlertsToDisplay } from 'modules/campaigns/lib/utils/alertUtils';
import { CampaignTypeFilter } from 'modules/domains/campaign';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ensureEnum } from 'utils/tsHelpers';
import { useCampaignIdParam, useCampaignStepsState, useQueryCampaignAlerts } from '../../hooks';
import { CampaignStep } from '../../types';
import { CampaignAlertsContext, CampaignAlertsContextState } from './CampaignAlertsContext';

/**
 * This context fetches any existing alerts created for the given campaign
 * as well as any enabled project-level alerts that match the campaign's
 * type and message medium.
 */
export const CampaignAlertsProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const campaignId = useCampaignIdParam();
  const { campaign } = useCampaignContext();
  const { campaignTypeDefinition, messageMedium, name } = campaign;
  const campaignType = ensureEnum(
    campaignTypeDefinition,
    CampaignTypeFilter,
    CampaignTypeFilter.Triggered,
  );

  const isCampaignPerformanceAlertsFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );
  const isCampaignPerformanceAlertsHistoryEnabled = useSelector(
    selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  );

  /** Common alert queries */

  const campaignAlertsQueryOptions = useMemo(
    () => ({
      skip: !campaignId || !isCampaignPerformanceAlertsFeatureEnabled,
      variables: { campaignId },
    }),
    [campaignId, isCampaignPerformanceAlertsFeatureEnabled],
  );

  const {
    isLoading: isLoadingAlerts,
    refetch: refetchCampaignAlerts,
    results: campaignAlerts,
  } = useQueryCampaignAlerts(campaignAlertsQueryOptions);

  const projectAlertsQueryOptions = useMemo(
    () => ({
      skip: !messageMedium || !isCampaignPerformanceAlertsFeatureEnabled,
      variables: {
        campaignType,
        messageMedium,
        status: AlertStatus.Enabled,
      },
    }),
    [isCampaignPerformanceAlertsFeatureEnabled, campaignType, messageMedium],
  );

  const { results: projectCampaignAlerts } =
    useProjectCampaignAlertsQuery(projectAlertsQueryOptions);

  const hasProjectAlerts = projectCampaignAlerts[messageMedium]?.length > 0;
  const alerts = getAlertsToDisplay(campaignAlerts, projectCampaignAlerts[messageMedium]);
  const enabledAlerts = alerts?.filter((alert) => alert.status === AlertStatus.Enabled);
  const alertDetails = getAlertDetails(enabledAlerts, campaignType);

  /** Alert takeover */

  const [isAlertTakeoverOpen, setIsAlertTakeoverOpen] = useState(false);

  const { viewCampaignStep, editCampaignStep } = useCampaignStepsState();

  const onTakeoverStateChange = (isOpen: boolean) => {
    if (isOpen) {
      editCampaignStep(CampaignStep.Delivery);
    } else {
      viewCampaignStep(CampaignStep.Delivery);
    }

    setIsAlertTakeoverOpen(isOpen);
  };

  const { campaignTriggeredAlerts } = useCampaignsHasRecentTriggeredAlerts({
    variables: {
      alertTimeWindow: RECENT_ALERTS_DURATION_WINDOW,
      campaignIds: [campaignId],
    },
    skip: !isCampaignPerformanceAlertsHistoryEnabled,
  });

  const value: CampaignAlertsContextState = {
    alertDetails,
    hasProjectAlerts,
    isLoadingAlerts,
    refetchCampaignAlerts,

    // takeover related
    campaignData: {
      campaignId,
      name,
      messageMedium,
      campaignType,
    },
    isAlertTakeoverOpen,
    onTakeoverOpen: onTakeoverStateChange,
    hasRecentTriggeredAlerts: !!campaignTriggeredAlerts?.[0]?.hasRecentAlerts,
  };

  return <CampaignAlertsContext.Provider value={value}>{children}</CampaignAlertsContext.Provider>;
};
